<template>
    <div>
        <div v-if="!canEditGuideCategory(guide)">
            {{ selectedGuideCategory.name }}
        </div>
        <pendo-guide-category-multiselect
            v-else
            data-cy="guide-details-subtitle-guide-category"
            :loading="loading"
            :selected-categories="[selectedGuideCategory]"
            :categories="guideCategoryOptions"
            :empty-option="NO_GUIDE_CATEGORY_OPTION"
            :allow-create-category="canManageGuideCategories"
            :allow-delete-category="canManageGuideCategories"
            :allow-edit-category="canManageGuideCategories"
            @edit="handleModalOpen(MODALS.upsertCategory, $event)"
            @delete="handleModalOpen(MODALS.deleteCategory, $event)"
            @create="handleModalOpen(MODALS.upsertCategory)"
            @input="handleSelect">
            <template #trigger>
                <pendo-data-measure-trigger class="pendo-guide-category__trigger" />
            </template>
        </pendo-guide-category-multiselect>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { PendoGuideCategoryMultiselect, PendoDataMeasureTrigger } from '@pendo/components';
import { isMobileApp } from '@/utils/apps';
import { canEditGuide } from '@/vuex/modules/guides/guide-permissions.utils';
import { i18n_t as $t } from '@utils/i18n';
import { MODALS } from '@/guides/constants/modals';

export const getNoGuideCategoryOption = () => ({ name: $t('guideCategories.No Guide Category') });

export default {
    name: 'GuideCategoryMultiselectCell',
    components: {
        PendoGuideCategoryMultiselect,
        PendoDataMeasureTrigger,
    },
    props: {
        guide: {
            type: Object,
            required: true,
        },
        platform: {
            type: String,
            default: 'web',
        },
    },
    emits: ['guide-category-change'],
    data () {
        return {
            loading: false,
            NO_GUIDE_CATEGORY_OPTION: {},
        };
    },
    computed: {
        ...mapGetters({
            guideCategoriesGroupedByMetaGroup: 'guideCategories/guideCategoriesGroupedByMetaGroup',
            isAdmin: 'auth/isAdmin',
            hasV2SubAdminAccess: 'auth/hasV2SubAdminAccess',
        }),
        canManageGuideCategories () {
            return this.isAdmin || this.hasV2SubAdminAccess;
        },
        categoryPlatform () {
            return isMobileApp(this.platform) ? 'mobile' : 'web';
        },
        selectedGuideCategory () {
            return this.guide.category || this.NO_GUIDE_CATEGORY_OPTION;
        },
        guideCategoryOptions () {
            return this.guideCategoriesGroupedByMetaGroup(this.categoryPlatform);
        },
    },
    created () {
        this.MODALS = MODALS;
        this.NO_GUIDE_CATEGORY_OPTION = getNoGuideCategoryOption();
    },
    methods: {
        ...mapActions({
            openModal: 'guideModals/openModal',
            removeGuideFromCategory: 'guideCategories/removeGuideFromCategory',
            addGuideToCategory: 'guideCategories/addGuideToCategory',
        }),
        async handleSelect (value) {
            const guideCategory = value[0];
            this.loading = true;

            try {
                await this.updateGuideCategory(guideCategory);
                this.$emit('guide-category-change', guideCategory);
            } catch (err) {
                console.error(`Failed to update guide category for ${this.guide.id}: ${err}`);
            }
            this.loading = false;
        },
        async updateGuideCategory (newGuideCategory) {
            const { id: guideId } = this.guide;

            if (!newGuideCategory.id) {
                await this.removeGuideFromCategory({ categoryId: this.selectedGuideCategory?.id, guideId });
            } else {
                await this.addGuideToCategory({
                    oldCategoryId: this.selectedGuideCategory?.id,
                    categoryId: newGuideCategory.id,
                    guideId,
                });
            }
        },
        handleModalOpen (modal, { id } = {}) {
            this.openModal({
                modal,
                props: {
                    categoryId: id || null,
                },
            });
        },
        canEditGuideCategory (guide) {
            const guideState = guide.state === 'scheduled' ? 'public' : guide.state;

            return canEditGuide({
                guide,
                source: guideState,
                target: guideState,
                field: 'group',
            });
        },
    },
};
</script>
<style lang="less" scoped>
.pendo-multiselect__trigger {
    color: unset;
    font-weight: unset;
    background: unset;
}
</style>
